/**
 * Push event to Google Tag Manager
 * @param category - Event category (or event object with category, action, label)
 * @param action - Event action
 * @param label - Event label containing feature, subFeature, and metadata
 * @param appName - Optional app name override
 */

type GTMMetaData = Record<string, unknown>;

export interface GTMLabel {
  feature?: string;
  subFeature?: string;
  metaData?: GTMMetaData;
  [key: string]: unknown;
}

export interface GTMEventInput {
  category: string;
  action: string;
  label?: GTMLabel;
  appName?: string | null;
}

interface GTMEventData {
  event: 'DQCS';
  category: string;
  action: string;
  user: {
    userUuid: string;
    userType: string;
    userName: string;
    sessionId: string;
  };
  language: {
    locale: string;
  };
  label: GTMLabel & {
    appName: string;
    metaData?: {
      sessionId: string;
    };
  };
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    alConfigInstance?: {
      storage?: {
        access_token_claims?: {
          userUuid?: string;
          accessType?: string;
        };
      };
    };
    __analyticsSessionId?: string;
  }
}

/**
 * Generates or retrieves a unique session identifier for the current browser session.
 * This ensures that different user sessions are tracked separately in Orbit,
 * even when userUuid is not available.
 */
function getOrCreateSessionId(): string {
  if (!window.__analyticsSessionId) {
    window.__analyticsSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analyticsSessionId', window.__analyticsSessionId);
  }
  return window.__analyticsSessionId;
}

export function pushToGTM(
  category: string | GTMEventInput,
  action?: string,
  label: GTMLabel = {},
  appName: string | null = null
): void {
  let eventCategory = '';
  let eventAction = '';
  let eventLabel: GTMLabel = label;
  let eventAppName: string | null = appName;

  // Support object parameter for convenience
  if (typeof category === 'object' && category !== null) {
    eventCategory = category.category;
    eventAction = category.action;
    eventLabel = category.label ?? {};
    eventAppName = category.appName ?? null;
  } else {
    eventCategory = category;
    eventAction = action ?? '';
  }

  // Get app name from localStorage or use fallback
  const analyticsAppName =
    eventAppName ||
    localStorage.getItem('analytics_appName') ||
    'apmt-am-ims';

  const analyticsUserName =
    sessionStorage.getItem('userName') ||
    JSON.parse(localStorage.getItem('userName') || '""') ||
    'N/A';

  // Get or create a unique session identifier to ensure different browser sessions
  // are tracked as separate users in Orbit, even when userUuid is not available
  const userUuidFromConfig = window.alConfigInstance?.storage?.access_token_claims?.userUuid;
  const sessionId = getOrCreateSessionId();

  // If userUuid is not available, use session ID to uniquely identify this browser session
  const userUuid = userUuidFromConfig || sessionId;

  const eventData: any = {
    event: 'DQCS',
    category: eventCategory,
    action: eventAction,
    user: {
      userUuid: userUuid,
      userType:
        window.alConfigInstance?.storage?.access_token_claims?.accessType ?? 'N/A',
      userName: analyticsUserName,
      sessionId: sessionId,
    },
    language: {
      locale: localStorage.getItem('preferredLanguage') || '',
    },
    label: {
      appName: analyticsAppName,
      ...eventLabel,
      metaData: {
        ...(eventLabel.metaData ?? {}),
        userName: analyticsUserName,
        sessionId: sessionId,
      },
    },
  };

  if (window.dataLayer) {
    window.dataLayer.push(eventData);
  } else {
    console.warn('GTM dataLayer is not available.');
  }
}

// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

/**
 * Track sidebar navigation clicks
 * @param targetPage - The page being navigated to
 * @param sourceLocation - Current location
 */
export function navigateSidebar(targetPage: string, sourceLocation = ''): void {
  pushToGTM('navigation', 'select', {
    feature: 'Sidebar',
    subFeature: 'Navigation',
    metaData: {
      targetPage,
      sourceLocation,
    },
  });
}

/**
 * Track breadcrumb navigation clicks
 * @param targetRoute - The route being navigated to
 * @param currentPage - Current page
 */
export function navigateBreadcrumb(targetRoute: string, currentPage = ''): void {
  pushToGTM('navigation', 'select', {
    feature: 'Breadcrumb',
    subFeature: 'Navigation',
    metaData: {
      targetRoute,
      currentPage,
    },
  });
}